import {Empty, isLoaded} from 'Core/models/Async';
import {
  changeOpportunityQuoteList,
  changeOpportunitySummary,
  getOpportunitySummary,
  setSelectedOpportunityId,
} from 'Quoting/ducks/opportunity';
import React, {useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router';
import {ReducerState} from 'rootReducer';
import {TabView} from '../shared/Tab/TabView';
import {OpportunitySummaryHeader} from './OpportunitySummaryHeader';
import {ProposalsTab} from './ProposalsTab';
import {QuotesTab} from './QuotesTab';
import {LocalStorageKey, toSessionStorage} from 'Shared/utilities/localStorage';
import {OpportunityQuote} from 'Quoting/models/DTOs/Opportunity';
import {QuoteStatus} from 'Quoting/models/enum';
import {applyQuoteListFilter} from 'Quoting/ducks/quoteActions';
import {FeatureConstants, getOrCreateFeatureFlag} from 'Core/ducks/featureFlag';

const filterQuotesByStatus = (quotes: OpportunityQuote[], quoteListFilter: QuoteStatus | 'active') => {
  if (quoteListFilter === 'active') {
    return quotes.filter(quote =>
      [QuoteStatus.Initialized, QuoteStatus.DataGather, QuoteStatus.Rated].includes(quote?.entity?.state as QuoteStatus)
    );
  }
  return quoteListFilter === QuoteStatus.AllStatus
    ? quotes
    : quotes.filter(quote => quote?.entity?.state === quoteListFilter);
};

export const OpportunitySummary = () => {
  const dispatch = useDispatch();
  const {opportunityId} = useParams<{opportunityId: string}>();
  const {quotes, proposals, opportunitySummary} = useSelector((state: ReducerState) => state.opportunity);
  const {quoteListFilter} = useSelector((state: ReducerState) => state.quoteActions);

  const customerName = isLoaded(opportunitySummary) ? opportunitySummary.Data.customerName : null;

  const filteredQuotes = useMemo(() => filterQuotesByStatus(isLoaded(quotes) ? quotes?.Data : [], quoteListFilter), [
    quoteListFilter,
    quotes,
  ]);

  useEffect(() => {
    dispatch(getOrCreateFeatureFlag(FeatureConstants.BR_QUOTING_TOOL_GPLU));
  }, []);

  const opportunitySummaryTabs = useMemo(() => {
    const quotesCount = `(${filteredQuotes.length ?? ''})`;
    const proposalsCount = proposals.key === 'Loaded' ? `(${proposals.Data.length})` : '';

    return [
      {title: `Quotes ${quotesCount}`, content: <QuotesTab filteredQuotes={filteredQuotes} />},
      {title: `Proposals ${proposalsCount}`, content: <ProposalsTab />},
    ];
  }, [opportunityId, proposals.key, filteredQuotes, filteredQuotes.length]);

  const opportunityProductsData = useMemo(
    () =>
      isLoaded(quotes)
        ? quotes.Data.reduce((acc, quote) => {
            if (quote.entity?.productCd) {
              acc.add(quote.entity?.productCd);
            }
            return acc;
          }, new Set<string>())
        : new Set<string>(),
    [opportunityId, quotes.key]
  );

  useEffect(() => {
    if (customerName) {
      toSessionStorage(LocalStorageKey.CustomerName)(customerName)();
    }
  }, []);

  useEffect(() => {
    if (opportunityId) {
      toSessionStorage(LocalStorageKey.OpportunityId)(opportunityId)();

      if (customerName) {
        toSessionStorage(LocalStorageKey.CustomerName)(customerName)();
      }
      dispatch(setSelectedOpportunityId(opportunityId));
      dispatch(getOpportunitySummary(opportunityId));
      dispatch(applyQuoteListFilter(QuoteStatus.AllStatus));
    }

    return () => {
      changeOpportunitySummary(new Empty());
      changeOpportunityQuoteList(new Empty());
    };
  }, [opportunityId]);

  if (!opportunityId) {
    return null;
  }

  return (
    <>
      <OpportunitySummaryHeader
        opportunitySummary={opportunitySummary}
        products={opportunityProductsData}
        opportunityId={opportunityId}
      />
      <TabView tabs={opportunitySummaryTabs} ariaLabel="opportunity-summary-tabs" key="opportunity-summary-tabs" />
    </>
  );
};
